import cookieCommons from 'common/cookieCommons';
import { PATH_NAME } from 'configs/pathName';
import { Breadcrumb } from 'design-system/components/Breadcrumb';
import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import {
  createContext,
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  useState,
} from 'react';
import LibraryFilterProvider, {
  EContentType,
} from 'state/search/LibraryFilterContext';
import TabPanel from '../../components/tabs/TabPanel';
import TabsCustomized from '../../components/tabs/TabsCustomized';
import FeedLayout from '../../layouts/FeedLayout/FeedLayout';

const LibraryTab = dynamic(
  () => import('@/components/tabs/search-feed/LibraryTab')
);
const AllTab = dynamic(() => import('@/components/tabs/search-feed/AllTab'));
const FriendTab = dynamic(
  () => import('@/components/tabs/search-feed/FriendTab')
);
const GroupTab = dynamic(
  () => import('@/components/tabs/search-feed/GroupTab')
);
const PostTab = dynamic(() => import('@/components/tabs/search-feed/PostTab'));

interface ISearchFeedContext {
  isPostLoading: boolean;
  isGroupLoading: boolean;
  isFriendLoading: boolean;
  isCourseLoading: boolean;
  isDocumentLoading: boolean;
  isCollectionLoading: boolean;
  setIsCollectionLoading: Dispatch<SetStateAction<boolean>>;
  setIsDocumentLoading: Dispatch<SetStateAction<boolean>>;
  setIsCourseLoading: Dispatch<SetStateAction<boolean>>;
  setIsPostLoading: Dispatch<SetStateAction<boolean>>;
  setIsGroupLoading: Dispatch<SetStateAction<boolean>>;
  setIsFriendLoading: Dispatch<SetStateAction<boolean>>;
  setCurrentTab: Dispatch<SetStateAction<number>>;
  currentTab: number;
  keyword: string;
  setKeyword: Dispatch<SetStateAction<string>>;
}
const defaultSearchFeedContext: ISearchFeedContext = {
  isPostLoading: true,
  isGroupLoading: true,
  isFriendLoading: true,
  setIsPostLoading: () => {},
  setIsGroupLoading: () => {},
  setIsFriendLoading: () => {},
  isCourseLoading: true,
  isDocumentLoading: true,
  isCollectionLoading: true,
  setIsCollectionLoading: () => {},
  setIsDocumentLoading: () => {},
  setIsCourseLoading: () => {},
  setCurrentTab: () => {},
  keyword: '',
  setKeyword: () => {},
  currentTab: 0,
};

export const SearchFeedContext = createContext<ISearchFeedContext>(
  defaultSearchFeedContext
);

const SearchHomeFeed = ({
  searchKey,
  tab = 0,
  contentType,
}: {
  searchKey: string;
  tab: number;
  contentType: string;
}) => {
  const [currentTab, setCurrentTab] = useState<number>(tab);
  const [isPostLoading, setIsPostLoading] = useState<boolean>(true);
  const [isGroupLoading, setIsGroupLoading] = useState<boolean>(true);
  const [isFriendLoading, setIsFriendLoading] = useState<boolean>(true);
  const [isCourseLoading, setIsCourseLoading] = useState(true);
  const [isDocumentLoading, setIsDocumentLoading] = useState(true);
  const [isCollectionLoading, setIsCollectionLoading] = useState(true);

  const [keyword, setKeyword] = useState(searchKey);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <SearchFeedContext.Provider
      value={{
        currentTab,
        isPostLoading,
        isGroupLoading,
        isFriendLoading,
        setIsPostLoading,
        setIsGroupLoading,
        setIsFriendLoading,
        isCourseLoading,
        setIsCourseLoading,
        isDocumentLoading,
        setIsDocumentLoading,
        setCurrentTab,
        keyword,
        setKeyword,
        isCollectionLoading,
        setIsCollectionLoading,
      }}
    >
      <LibraryFilterProvider contentType={contentType}>
        <FeedLayout>
          <div className="bg-white">
            <div className="mx-2 md:mx-4">
              <div className="w-full space-y-2 border-b border-Other/Outlinedborder-stroke bg-white px-3 pt-2 lg:pt-4 xl:space-y-4 xl:px-10">
                <Breadcrumb
                  breadcrumbItems={[
                    { name: 'Khám phá', path: PATH_NAME.HOME },
                    {
                      name: 'Kết quả tìm kiếm',
                      path: {
                        pathname: PATH_NAME.SEARCH,
                        query: {
                          searchKey: keyword,
                        },
                      },
                    },
                  ]}
                  startPlace={{
                    iconName: 'discover',
                    title: 'Khám phá',
                  }}
                />

                <TabsCustomized
                  tabList={[
                    'Tất cả',
                    'Thư viện Doccen',
                    'Bài viết',
                    'Nhóm',
                    'Bạn bè',
                  ]}
                  value={currentTab}
                  handleChange={handleChange}
                />
              </div>
            </div>
          </div>
          <div className="bg-white">
            {/* tabs */}
            <div className="w-full">
              {/* All tab */}
              <TabPanel value={currentTab} index={0}>
                <AllTab />
              </TabPanel>
              {/* Library tab */}
              <TabPanel value={currentTab} index={1}>
                <LibraryTab />
              </TabPanel>
              {/* Post tab */}
              <TabPanel value={currentTab} index={2}>
                <PostTab />
              </TabPanel>
              {/* Group tab */}
              <TabPanel value={currentTab} index={3}>
                <GroupTab />
              </TabPanel>
              {/* Friend tab */}
              <TabPanel value={currentTab} index={4}>
                <FriendTab />
              </TabPanel>
            </div>
          </div>
        </FeedLayout>
      </LibraryFilterProvider>
    </SearchFeedContext.Provider>
  );
};

export default SearchHomeFeed;

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  cookieCommons.setContext(context);
  const { searchKey, tab, contentType } = context.query;

  return {
    props: {
      searchKey: searchKey || '',
      tab: tab ? parseInt(tab as string) : 0,
      contentType: contentType || EContentType.All,
    },
  };
};
